import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import invariant from 'invariant';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import conformsTo from 'lodash/conformsTo';

import { Loader } from 'components/Loader';
import NotFoundPage from 'pages/NotFoundPage/NotFoundPage';
import { selectIsLoginComplete } from 'routes/AppContainer/selectors';
import { useInjectReducers } from 'utils/injectReducer';
import { useInjectSagas } from 'utils/injectSaga';

import { RESTART_ON_REMOUNT } from './constants';
import { WrappedRoute } from './WrappedRoute';

// For routes with nested routes (e.g. org container), render only the Switch
// so there is a single source of content and NotFoundPage is not rendered twice.
// NestedRoutesContainer handles reducer/saga injection and then renders the Switch.
const NestedRoutesContainer = ({ routeConfig, noAuth, createRouteFn }) => {
  const { reducers = [], sagas = [], routes = [], name } = routeConfig;
  const isLoginComplete = useSelector((state) =>
    selectIsLoginComplete()(state),
  );
  const [areReducersInjected, setAreReducersInjected] = useState(
    !reducers.length,
  );
  const [areSagasInjected, setAreSagasInjected] = useState(!sagas.length);

  const injectorReadyCallback = useCallback(() => {
    setAreReducersInjected(true);
  }, []);
  useInjectReducers(reducers, injectorReadyCallback);

  const newSagas = sagas.map((s) => ({
    ...s,
    mode: s.mode || RESTART_ON_REMOUNT,
  }));
  const sagaReadyCallback = useCallback(() => {
    setAreSagasInjected(true);
  }, []);
  useInjectSagas(newSagas, sagaReadyCallback);

  useEffect(() => {
    return () => {
      setAreReducersInjected(false);
      setAreSagasInjected(false);
    };
  }, [name]);

  const loginCheck = noAuth || isLoginComplete;
  if (!(areReducersInjected && areSagasInjected && loginCheck)) {
    return (
      <div style={{ marginTop: '55px' }}>
        <Loader loaderContentOnly />
      </div>
    );
  }

  return (
    <Switch>
      {routes.map((r) => createRouteFn(r, noAuth))}
      <Route path="*">
        <NotFoundPage />
      </Route>
    </Switch>
  );
};

NestedRoutesContainer.propTypes = {
  routeConfig: PropTypes.shape({
    reducers: PropTypes.array,
    sagas: PropTypes.array,
    routes: PropTypes.array,
    name: PropTypes.string,
  }).isRequired,
  noAuth: PropTypes.bool,
  createRouteFn: PropTypes.func.isRequired,
};

// //////////////////////////////////////////
// this is the actual exported function that
// creates a <WrappedRoute />
// used in react router to dynamically create routes
// and handle injection
// //////////////////////////////////////////

export function createRoute(routeConfig, noAuth = false) {
  const shape = {
    RouteComponent: (value) => isObject(value) || isArray(value),
    scope: isArray,
    reducers: isArray,
    sagas: isArray,
    path: (value) => isString(value) || isArray(value),
  };

  const optionalDefaults = {
    RouteComponent: () => null,
    exact: true,
    reducers: [],
    sagas: [],
    scope: [],
  };

  // Some helpful feedback if someone creates a route without valid params
  const judge = { ...optionalDefaults, ...routeConfig };
  invariant(
    conformsTo(judge, shape),
    `(utils/createRoute.js) createRoute: Expected a valid routeConfig object ${
      routeConfig ? routeConfig.name : 'MISSING NAME'
    } - ${JSON.stringify(Object.keys(routeConfig))}`,
  );

  const hasNestedRoutes = !!routeConfig.routes?.length;

  return (
    <Route
      key={`route-${routeConfig.name}`}
      path={
        routeConfig.exact
          ? routeConfig.path
          : isArray(routeConfig.path)
            ? routeConfig.path.map((str) => `*${str}`)
            : `*${routeConfig.path}`
      }
      exact={routeConfig.exact}
    >
      {hasNestedRoutes ? (
        <NestedRoutesContainer
          routeConfig={routeConfig}
          noAuth={noAuth}
          createRouteFn={createRoute}
        />
      ) : (
        <WrappedRoute noAuth={noAuth} {...routeConfig} />
      )}
    </Route>
  );
}
